import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import ArtistSummary from '../ArtistSummary';
import "./navbar.css";

export default function Navbar({ isAuthenticated, username, onSignOut }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  const navigate = useNavigate();

  // Debug logging for authentication state
  useEffect(() => {
    console.log('🔍 Navbar - Authentication state changed:', {
      isAuthenticated,
      username,
      hasOnSignOut: !!onSignOut
    });
  }, [isAuthenticated, username, onSignOut]);

  // Close any dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const groups = [
    {
      key: "workspace",
      label: "Workspace",
      items: [
        { to: "/workspace", label: "Dashboard" },
        { to: "/workspace/social", label: "Social" },
      ],
    },
    {
      key: "catalog",
      label: "Catalog",
      items: [
        { to: "/catalog", label: "Catalog" },
        { to: "/catalog/showcase", label: "Showcase" },
        { to: "/catalog/library", label: "Library" },
        { to: "/catalog/simple", label: "Simple" },
      ],
    },
    {
      key: "insights",
      label: "Insights",
      items: [
        { to: "/insights", label: "Analytics" },
        { to: "/insights/spotify", label: "Spotify" },
      ],
    },
    {
      key: "growth",
      label: "Growth",
      items: [
        { to: "/growth/viral-automation", label: "Content Automation" },
        { to: "/growth/intelligent-pitch", label: "Intelligent Pitch" },
      ],
    },
    {
      key: "admin",
      label: "Admin",
      items: [
        { to: "/admin", label: "Business Dashboard" },
      ],
    },
  ];

  const secondary = [
    { to: "/about", label: "Why Artists Choose Us" },
    { to: "/contact", label: "Join the Movement" },
  ];

  function GroupButton({ group }) {
    const isOpen = openMenu === group.key;
    return (
      <div className="nav-group">
        <button
          className={`nav-trigger ${isOpen ? "open" : ""}`}
          onClick={() => setOpenMenu(isOpen ? null : group.key)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpenMenu(null);
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {group.label}
          <span className="chev" aria-hidden>▾</span>
        </button>
        {isOpen && (
          <div className="dropdown" role="menu">
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  "drop-link" + (isActive ? " active" : "")
                }
                onClick={() => {
                  setOpenMenu(null);
                  setMobileOpen(false);
                }}
                role="menuitem"
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <header className="dm-nav" ref={navRef}>
      <div className="nav-inner">
        <div className="brand" onClick={() => navigate("/")}>
          <img src="/logo192.png" alt="" className="brand-logo" />
          <span className="brand-name">decoded music</span>
        </div>

        <button
          className="hamburger"
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={`nav-main ${mobileOpen ? "show" : ""}`}>
          {isAuthenticated ? (
            <>
              <div className="nav-groups">
                {groups.map((g) => (
                  <GroupButton key={g.key} group={g} />
                ))}
              </div>

              <div className="nav-secondary">
                <div className="user-menu">
                  <button
                    className="user-chip"
                    onClick={() => setOpenMenu(openMenu === "user" ? null : "user")}
                    aria-haspopup="true"
                    aria-expanded={openMenu === "user"}
                  >
                    <span className="avatar" aria-hidden>
                      {username?.[0]?.toUpperCase() || "A"}
                    </span>
                    <span className="uname">{username || "Artist"}</span>
                    <span className="chev" aria-hidden>▾</span>
                  </button>
                  {openMenu === "user" && (
                    <div className="dropdown right" role="menu">
                      <NavLink
                        to="/workspace"
                        className="drop-link"
                        onClick={() => setOpenMenu(null)}
                        role="menuitem"
                      >
                        Go to Dashboard
                      </NavLink>
                      <button
                        className="drop-link danger"
                        onClick={() => {
                          setOpenMenu(null);
                          onSignOut?.();
                        }}
                        role="menuitem"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="nav-groups">
              {secondary.map((s) => (
                <NavLink
                  key={s.to}
                  to={s.to}
                  className="link-secondary"
                  onClick={() => setMobileOpen(false)}
                >
                  {s.label}
                </NavLink>
              ))}
              <NavLink to="/login" className="btn btn-primary">
                Login
              </NavLink>
            </div>
          )}
        </nav>
      </div>
      
      {/* Artist Summary Component - shows when authenticated */}
      {isAuthenticated && <ArtistSummary />}
    </header>
  );
}

